// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import styles from './GroupList.module.css';

export interface GroupListItem {
  id: string;
  name: string;
  /** The muted line under the name ("4 medlemmer"). */
  detail: string;
  /** A short tag on the right ("Du er med"); omitted when there is none. */
  badge?: string;
  /** Emphasise the badge (the caller is in this group). */
  strong?: boolean;
}

/**
 * A selectable list of named items — the Groups page's group picker. One
 * button per item; the selected one is `aria-current`.
 */
export function GroupList({
  items,
  selectedId,
  onSelect,
  label,
  empty,
}: {
  items: GroupListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label: string;
  empty?: ReactNode;
}) {
  if (items.length === 0) return <>{empty}</>;
  return (
    <ul className={styles.list} aria-label={label}>
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={styles.item}
            aria-current={item.id === selectedId ? 'true' : undefined}
            onClick={() => onSelect(item.id)}
          >
            <span className={styles.text}>
              <span className={styles.name}>{item.name}</span>
              <span className={styles.detail}>{item.detail}</span>
            </span>
            {item.badge && (
              <span className={styles.badge} data-strong={item.strong ? 'true' : undefined}>
                {item.badge}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
